import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SMLogo from '../SMLogo';
import useScrollPosition from '../../utils/useScrollPosition';
import { useIsMD } from '../../utils/useResponsive';
import NavItem from './NavItem';
import { motion } from 'framer-motion';
import Hamburger from 'hamburger-react';
import { Link, useLocation } from 'react-router-dom';
import { SprawneMiastoContext } from '../SprawneMiastoContext';
import LoginRegister from './LoginRegister';
import Dropdown from '../Dropdown';
import { MdAssignmentTurnedIn, MdExpandMore, MdOutlineAccountCircle } from 'react-icons/md';

export default function Navbar(props: { notAffectOn: string[] }) {
    const { t } = useTranslation();
    const scroll = useScrollPosition();
    const isMobile = !useIsMD();
    const [showMobile, setShowMobile] = useState<boolean>(true);
    const location = useLocation();
    const [showLoginRegister, setShowLoginRegister] = useState<boolean>(false);
    const Context = useContext(SprawneMiastoContext);
    useEffect(() => {
        setShowMobile(true);
    }, [location.pathname]);

    return (
        <>
            <LoginRegister
                open={showLoginRegister}
                onClose={() => {
                    setShowLoginRegister(false);
                }}
            />
            {isMobile ? (
                <div
                    className={`fixed z-20 flex w-full w-full flex-col overflow-hidden bg-SM-dark text-white transition-all ${
                        scroll === 0 ? 'min-h-[72px] py-3 px-7' : 'py-2 px-6'
                    } duration-150 `}>
                    <div className={'flex items-center'}>
                        <SMLogo className={'my-2'} />

                        <div className={'ml-auto flex items-center'}>
                            <Hamburger
                                toggled={!showMobile}
                                toggle={() => {
                                    setShowMobile(!showMobile);
                                }}
                                hideOutline={false}
                            />
                        </div>
                    </div>

                    <motion.div
                        animate={showMobile ? 'hidden' : 'visible'}
                        variants={{
                            visible: { height: '100%' },
                            hidden: { height: '0px', visibility: 'hidden' },
                        }}
                        transition={{ duration: 0.2 }}
                        className={'flex w-full flex-col gap-2'}>
                        <NavItem text={t('NAVBAR.ABOUT_US')} to={'/about-us'} />
                        <NavItem text={t('NAVBAR.PARTNERS')} to={'/partners'} />
                        <NavItem text={t('NAVBAR.HOW_IT_WORKS')} to={'/how-it-works'} />
                        <NavItem text={t('NAVBAR.CONTACT')} to={'/contact'} />
                        {Context?.user == null ? (
                            <button
                                className={'hover-effect text-left text-lg'}
                                onClick={() => {
                                    setShowLoginRegister(true);
                                }}>
                                {t('NAVBAR.LOGIN')}
                            </button>
                        ) : (
                            <div className={'flex flex-col rounded bg-slate-700 py-2'}>
                                <Link
                                    to={'/my-account/profile'}
                                    className={
                                        'flex items-center gap-2 py-2 px-4 text-sm hover:bg-slate-600'
                                    }>
                                    <MdOutlineAccountCircle className={'text-lg'} />
                                    {t('translation:NAVBAR.MY_ACCOUNT')}
                                </Link>

                                <Link
                                    to={'/my-account/my-reports'}
                                    className={
                                        'flex items-center gap-2 py-2 px-4 text-sm hover:bg-slate-600'
                                    }>
                                    <MdAssignmentTurnedIn className={'text-md'} />
                                    {t('translation:NAVBAR.MY_REPORTS')}
                                </Link>

                                <hr className={'my-2'} />

                                <button
                                    className={'text-sm hover:text-red-500'}
                                    onClick={() => Context.user?.logout(false)}>
                                    {t('translation:NAVBAR.LOGOUT')}
                                </button>
                            </div>
                        )}
                    </motion.div>
                </div>
            ) : (
                <div
                    className={`fixed z-20 flex w-full items-center gap-5 bg-SM-dark text-white transition-all ${
                        scroll === 0 ? 'min-h-[72px] py-3 px-7' : 'py-2 px-6'
                    } duration-150 `}>
                    <SMLogo className={'my-2'} />
                    <div className={'flex w-full gap-3'}>
                        <NavItem text={t('NAVBAR.ABOUT_US')} to={'/about-us'} />
                        <NavItem text={t('NAVBAR.PARTNERS')} to={'/partners'} />
                        <NavItem text={t('NAVBAR.HOW_IT_WORKS')} to={'/how-it-works'} />
                        <NavItem text={t('NAVBAR.CONTACT')} to={'/contact'} />
                        {Context?.user === undefined ? (
                            <button
                                className={'hover-effect ml-auto text-lg'}
                                onClick={() => {
                                    setShowLoginRegister(true);
                                }}>
                                {t('NAVBAR.LOGIN')}
                            </button>
                        ) : (
                            <Dropdown
                                title={
                                    <span className={'text-md flex items-center'}>
                                        {Context.user.username}
                                        <MdExpandMore className={'text-2xl'} />
                                    </span>
                                }
                                className={'ml-auto'}>
                                <div
                                    className={
                                        'flex flex-col whitespace-nowrap rounded bg-slate-700 py-2'
                                    }>
                                    <Link
                                        to={'/my-account/profile'}
                                        className={
                                            'flex items-center gap-2 py-2 px-4 text-sm hover:bg-slate-600'
                                        }>
                                        <MdOutlineAccountCircle className={'text-lg'} />
                                        {t('translation:NAVBAR.MY_ACCOUNT')}
                                    </Link>

                                    <Link
                                        to={'/my-account/my-reports'}
                                        className={
                                            'flex items-center gap-2 py-2 px-4 text-sm hover:bg-slate-600'
                                        }>
                                        <MdAssignmentTurnedIn className={'text-md'} />
                                        {t('translation:NAVBAR.MY_REPORTS')}
                                    </Link>

                                    <hr className={'my-2'} />

                                    <button
                                        className={'text-sm hover:text-red-500'}
                                        onClick={() => Context.user?.logout(false)}>
                                        {t('translation:NAVBAR.LOGOUT')}
                                    </button>
                                </div>
                            </Dropdown>
                        )}
                    </div>
                </div>
            )}
            {!props.notAffectOn.includes(location.pathname) && (
                <div style={{ height: '72px' }} className={'bg-SM-dark'} />
            )}
        </>
    );
}
